import React, { useState, useRef, useEffect } from 'react';
import { Group, Line, Text, Path, Rect } from 'react-konva';
import Konva from 'konva';
import { SequenceMessage, ThemePalette, KonvaMouseEvent, KonvaInteractionEvent } from '../../types';
import { EDGE } from '../../constants/edges';
import { FONT_FAMILY } from '../../constants/typography';
import { formatSequenceMessageLabel } from '../../utils/sequenceAppearance';

const SELF_MSG_WIDTH = 48;
const SELF_MSG_HEIGHT = 36;
const LABEL_LINE_HEIGHT = 14;
const LABEL_FONT_SIZE = 13;
const LABEL_PADDING_X = 8;
const LABEL_PADDING_Y = 4;
const LABEL_GAP = 6;
const LABEL_CHAR_WIDTH_ESTIMATE = 5.9;
const MIN_LABEL_WIDTH = 96;

function estimateWrappedLineCount(text: string, maxWidth: number): number {
    const safeText = text?.trim() ?? '';
    if (!safeText) return 1;
    const charsPerLine = Math.max(8, Math.floor(maxWidth / LABEL_CHAR_WIDTH_ESTIMATE));
    return safeText.split(/\r?\n/).reduce((total, rawLine) => {
        const line = rawLine.trim();
        if (!line) return total + 1;
        return total + Math.max(1, Math.ceil(line.length / charsPerLine));
    }, 0);
}

interface MessageComponentProps {
    message: SequenceMessage;
    sourceX: number;
    targetX: number;
    selfMessageLabelMaxWidth?: number;
    isSelected: boolean;
    onMessageClick: (id: string, e: KonvaInteractionEvent) => void;
    onMessageDoubleClick: (id: string, e: KonvaInteractionEvent) => void;
    onMessageContextMenu?: (id: string, x: number, y: number) => void;
    onMessageDragEnd?: (id: string, newY: number) => void;
    onMessageDragComplete?: (id: string) => void;
    onMessageHover?: (id: string | null, screenX: number, screenY: number) => void;
    hideLabel: boolean;
    palette: ThemePalette;
    zoomScale: number;
    opacity?: number;
    isReadOnly?: boolean;
    /** When false, skip per-pixel tooltip tracking (avoids App re-renders on mousemove). */
    hasTooltip?: boolean;
    sequenceNumber?: number;
}

const MessageComponentInner: React.FC<MessageComponentProps> = ({
    message,
    sourceX,
    targetX,
    selfMessageLabelMaxWidth,
    isSelected,
    onMessageClick,
    onMessageDoubleClick,
    onMessageContextMenu,
    onMessageDragEnd,
    onMessageDragComplete,
    onMessageHover,
    hideLabel,
    palette,
    zoomScale,
    opacity = 1,
    isReadOnly = false,
    hasTooltip = false,
    sequenceNumber,
}) => {
    const handleContextMenu = (e: KonvaMouseEvent) => {
        e.evt.preventDefault();
        if (!onMessageContextMenu) return;
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (pos) onMessageContextMenu(message.id, pos.x, pos.y);
    };

    const [isHovered, setIsHovered] = useState(false);
    const glowRefSelf = useRef<Konva.Line>(null);
    const glowRefMain = useRef<Konva.Line>(null);
    const isSelfMessage = sourceX === targetX;
    const displayLabel = formatSequenceMessageLabel(message.label, sequenceNumber);

    // Cache glow lines so Konva.Filters.Blur renders correctly (matches EdgeComponent approach)
    useEffect(() => {
        if (glowRefSelf.current && isSelected) {
            glowRefSelf.current.cache();
        }
    }, [isSelected]);
    useEffect(() => {
        if (glowRefMain.current && isSelected) {
            glowRefMain.current.cache();
        }
    }, [isSelected]);

    const baseColor = message.color ?? palette.edgeDefault;
    const hoverColor = message.color ?? palette.edgeSelected;
    const strokeColor = isSelected ? baseColor : (isHovered ? hoverColor : baseColor);
    const labelColor = message.color ?? palette.nodeText;
    const baseStrokeW = message.strokeWidth ?? EDGE.DEFAULT_SEQUENCE_MESSAGE_STROKE_WIDTH;
    const strokeW = baseStrokeW + (isHovered ? 1 : 0);
    const glowStrokeW = strokeW + 2.5;
    const isReturn = message.type === 'return';
    const effectiveDashStyle = message.dashStyle ?? (isReturn ? 'dashed' : 'solid');
    const dash = EDGE.DASH_PATTERNS[effectiveDashStyle];
    const dashProps = dash ? { dash } : {};
    const arrowSize = 12;
    const arrowOffset = 6;

    const reportPointer = (e: KonvaInteractionEvent) => {
        const p = e.target.getStage()?.getPointerPosition();
        if (p) onMessageHover?.(message.id, p.x, p.y);
    };

    const hoverHandlers = {
        onMouseEnter: (e: KonvaInteractionEvent) => {
            setIsHovered(true);
            reportPointer(e);
        },
        onMouseMove: hasTooltip ? reportPointer : undefined,
        onMouseLeave: () => {
            setIsHovered(false);
            onMessageHover?.(null, 0, 0);
        },
    };

    if (isSelfMessage) {
        const x0 = sourceX;
        const y0 = message.y;
        const x1 = sourceX + SELF_MSG_WIDTH;
        const y1 = message.y + SELF_MSG_HEIGHT;
        const returnX = sourceX;
        const tipX = returnX + arrowOffset;
        const loopPoints = [x0, y0, x1, y0, x1, y1, tipX + arrowSize, y1];

        const half = arrowSize * 0.55;
        const arrowHead = `M ${tipX} ${y1} L ${tipX + arrowSize} ${y1 - half} L ${tipX + arrowSize} ${y1 + half} Z`;
        const maxSelfLabelWidth = Math.max(
            120,
            Math.min(selfMessageLabelMaxWidth ?? (SELF_MSG_WIDTH + 24), 560),
        );
        const wrappedLineCount = estimateWrappedLineCount(displayLabel, maxSelfLabelWidth - LABEL_PADDING_X * 2);
        const labelHeight = wrappedLineCount * LABEL_LINE_HEIGHT + LABEL_PADDING_Y * 2;
        const labelTopY = y0 - labelHeight - LABEL_GAP;

        return (
            <Group
                {...hoverHandlers}
                onClick={(e) => onMessageClick(message.id, e)}
                onTap={(e) => onMessageClick(message.id, e)}
                onDblClick={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
                onContextMenu={handleContextMenu}
                opacity={opacity}
            >
                <Rect
                    x={x0 - 5}
                    y={y0 - 5}
                    width={SELF_MSG_WIDTH + 10}
                    height={SELF_MSG_HEIGHT + 10}
                    fill="transparent"
                    perfectDrawEnabled={false}
                    draggable={!isReadOnly}
                    dragBoundFunc={(pos) => ({ x: pos.x, y: pos.y })}
                    onDragMove={(e) => {
                        if (!onMessageDragEnd) return;
                        const pointer = e.target.getStage()?.getPointerPosition();
                        const viewportGroup = e.target.getParent()?.getParent();
                        const transform = viewportGroup?.getAbsoluteTransform().copy().invert();
                        if (pointer && transform) {
                            const canvasY = transform.point(pointer).y;
                            onMessageDragEnd(message.id, canvasY);
                        }
                    }}
                    onDragEnd={(e) => {
                        e.target.position({ x: x0 - 5, y: y0 - 5 });
                        if (onMessageDragComplete) onMessageDragComplete(message.id);
                    }}
                />

                {isSelected && (
                    <Line
                        ref={glowRefSelf}
                        points={loopPoints}
                        stroke="white"
                        strokeWidth={glowStrokeW + 4}
                        opacity={0.22}
                        filters={isSelected ? [Konva.Filters.Blur] : []}
                        blurRadius={10}
                        listening={false}
                        perfectDrawEnabled={false}
                        shadowForStrokeEnabled={false}
                    />
                )}

                <Line
                    points={loopPoints}
                    stroke={strokeColor}
                    strokeWidth={strokeW}
                    {...dashProps}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />

                <Path
                    data={arrowHead}
                    fill={message.type === 'sync' ? strokeColor : palette.canvasBg}
                    stroke={strokeColor}
                    strokeWidth={Math.max(1.5, strokeW * 0.8)}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />

                {!hideLabel && (
                    <>
                        <Rect
                            x={x0 + 4}
                            y={labelTopY}
                            width={maxSelfLabelWidth}
                            height={labelHeight}
                            fill={palette.canvasBg}
                            opacity={0.3}
                            cornerRadius={4}
                            listening={false}
                            perfectDrawEnabled={false}
                        />
                        <Text
                            text={displayLabel}
                            x={x0 + 4}
                            y={labelTopY + LABEL_PADDING_Y}
                            width={maxSelfLabelWidth}
                            height={labelHeight - LABEL_PADDING_Y * 2}
                            align="left"
                            fill={labelColor}
                            fontSize={13}
                            fontFamily={FONT_FAMILY}
                            lineHeight={LABEL_LINE_HEIGHT / LABEL_FONT_SIZE}
                            fontStyle={isReturn ? 'italic' : 'normal'}
                            perfectDrawEnabled={false}
                            onClick={(e) => onMessageClick(message.id, e)}
                            onTap={(e) => onMessageClick(message.id, e)}
                            onDblClick={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
                            onDblTap={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
                        />
                    </>
                )}
            </Group>
        );
    }

    const isLeftToRight = targetX > sourceX;
    const actualSourceX = isLeftToRight ? sourceX + 4 : sourceX - 4;
    const arrowTipX = isLeftToRight ? targetX - arrowOffset : targetX + arrowOffset;
    const arrowBaseX = isLeftToRight ? arrowTipX - arrowSize : arrowTipX + arrowSize;
    const actualTargetX = arrowBaseX;

    const arrowHead = `M ${arrowTipX} ${message.y}
                     L ${arrowBaseX} ${message.y - arrowSize * 0.65}
                     L ${arrowBaseX} ${message.y + arrowSize * 0.65} Z`;
    const labelRangeMinX = Math.min(actualSourceX, arrowTipX);
    const labelRangeMaxX = Math.max(actualSourceX, arrowTipX);
    const lineSpan = Math.max(40, labelRangeMaxX - labelRangeMinX);
    const maxLabelWidth = Math.max(MIN_LABEL_WIDTH, lineSpan - 12);
    const wrappedLineCount = estimateWrappedLineCount(displayLabel, maxLabelWidth - LABEL_PADDING_X * 2);
    const labelHeight = wrappedLineCount * LABEL_LINE_HEIGHT + LABEL_PADDING_Y * 2;
    const labelTopY = message.y - labelHeight - LABEL_GAP;
    const centeredLabelX = labelRangeMinX + (lineSpan - maxLabelWidth) / 2;

    return (
        <Group
            {...hoverHandlers}
            onClick={(e) => onMessageClick(message.id, e)}
            onTap={(e) => onMessageClick(message.id, e)}
            onDblClick={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
            onContextMenu={handleContextMenu}
            opacity={opacity}
        >
            <Line
                points={[actualSourceX, message.y, arrowTipX, message.y]}
                stroke="transparent"
                strokeWidth={20 / zoomScale}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
                draggable={!isReadOnly}
                dragBoundFunc={(pos) => ({ x: pos.x, y: pos.y })}
                onDragMove={(e) => {
                    if (!onMessageDragEnd) return;
                    const pointer = e.target.getStage()?.getPointerPosition();
                    const viewportGroup = e.target.getParent()?.getParent();
                    const transform = viewportGroup?.getAbsoluteTransform().copy().invert();
                    if (pointer && transform) {
                        const canvasY = transform.point(pointer).y;
                        onMessageDragEnd(message.id, canvasY);
                    }
                }}
                onDragEnd={(e) => {
                    e.target.position({ x: 0, y: 0 });
                    if (onMessageDragComplete) onMessageDragComplete(message.id);
                }}
            />

            {isSelected && (
                <Line
                    ref={glowRefMain}
                    points={[actualSourceX, message.y, actualTargetX, message.y]}
                    stroke="white"
                    strokeWidth={glowStrokeW + 4}
                    {...dashProps}
                    opacity={0.22}
                    filters={isSelected ? [Konva.Filters.Blur] : []}
                    blurRadius={10}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />
            )}

            <Line
                points={[actualSourceX, message.y, actualTargetX, message.y]}
                stroke={strokeColor}
                strokeWidth={strokeW}
                {...dashProps}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
            />

            <Path
                data={arrowHead}
                fill={message.type === 'sync' ? strokeColor : palette.canvasBg}
                stroke={strokeColor}
                strokeWidth={Math.max(1.5, strokeW * 0.8)}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
            />

            {!hideLabel && (
                <>
                    <Rect
                        x={centeredLabelX}
                        y={labelTopY}
                        width={maxLabelWidth}
                        height={labelHeight}
                        fill={palette.canvasBg}
                        opacity={0.3}
                        cornerRadius={4}
                        listening={false}
                        perfectDrawEnabled={false}
                    />
                    <Text
                        text={displayLabel}
                        x={centeredLabelX}
                        y={labelTopY + LABEL_PADDING_Y}
                        width={maxLabelWidth}
                        height={labelHeight - LABEL_PADDING_Y * 2}
                        align="center"
                        wrap="word"
                        fill={labelColor}
                        fontSize={LABEL_FONT_SIZE}
                        fontFamily={FONT_FAMILY}
                        lineHeight={LABEL_LINE_HEIGHT / LABEL_FONT_SIZE}
                        fontStyle={isReturn ? 'italic' : 'normal'}
                        perfectDrawEnabled={false}
                        onClick={(e) => onMessageClick(message.id, e)}
                        onTap={(e) => onMessageClick(message.id, e)}
                        onDblClick={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
                        onDblTap={(e) => { if (!isReadOnly) onMessageDoubleClick(message.id, e); }}
                    />
                </>
            )}
        </Group>
    );
};

export const MessageComponent = React.memo(MessageComponentInner);

export { SELF_MSG_WIDTH };
